import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { BookingAgentService } from 'src/app/services/booking-agent/booking-agent.service';
import { BookingAgentModalService } from 'src/app/services/booking-agent-modal/booking-agent-modal.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-add-edit-booking-agent',
  templateUrl: './add-edit-booking-agent.component.html',
  styleUrls: ['./add-edit-booking-agent.component.scss']
})
export class AddEditBookingAgentComponent extends DestructorComponent implements OnInit {

  @Output() bookingAgentAdded = new EventEmitter<void>();
  @Output() bookingAgentUpdated = new EventEmitter<void>();

  bookingAgentForm!: FormGroup;
  isLoading = false;
  isEditMode = false;
  selectedBookingAgent: any = null;

  constructor(
    private fb: FormBuilder,
    private _bookingAgentService: BookingAgentService,
    private _bookingAgentModalService: BookingAgentModalService
  ) {
    super();
    this.initializeForm();
  }

  ngOnInit(): void {
    this.setupModalListener();
  }

  private initializeForm(): void {
    this.bookingAgentForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [Validators.email]],
      phone_number: ['']
    });
  }

  // Getter methods for form controls
  get name() { return this.bookingAgentForm.get('name'); }
  get email() { return this.bookingAgentForm.get('email'); }
  get phone_number() { return this.bookingAgentForm.get('phone_number'); }

  private setupModalListener(): void {
    const modalElement = document.getElementById('kt_modal_booking_agent_add_edit');
    if (modalElement) {
      modalElement.addEventListener('shown.bs.modal', () => {
        const serviceBookingAgent = this._bookingAgentModalService.getSelectedBookingAgent();
        if (serviceBookingAgent) {
          this.isEditMode = true;
          this.selectedBookingAgent = serviceBookingAgent;
          this.populateForm(this.selectedBookingAgent);
        } else {
          this.isEditMode = false;
          this.selectedBookingAgent = null;
          this.resetForm();
        }
      });

      modalElement.addEventListener('hidden.bs.modal', () => {
        this._bookingAgentModalService.clearSelectedBookingAgent();
        this.resetForm();
      });
    }
  }

  private populateForm(bookingAgent: any): void {
    this.bookingAgentForm.patchValue({
      name: bookingAgent.name || '',
      email: bookingAgent.email || '',
      phone_number: bookingAgent.phone_number || ''
    });
  }

  private resetForm(): void {
    this.bookingAgentForm.reset();
    this.isLoading = false;
    this.markFormGroupUntouched(this.bookingAgentForm);
  }

  private markFormGroupUntouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsUntouched();
      control?.markAsPristine();
    });
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  private closeModal(): void {
    const modalElement = document.getElementById('kt_modal_booking_agent_add_edit');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  onSubmit(): void {
    if (this.bookingAgentForm.valid && !this.isLoading) {
      this.isLoading = true;

      const formData = this.bookingAgentForm.value;

      // Remove empty fields
      const submitData: any = {};
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== '') {
          submitData[key] = formData[key];
        }
      });

      const apiCall = this.isEditMode
        ? this._bookingAgentService.UpdateData(this.selectedBookingAgent._id, submitData)
        : this._bookingAgentService.AddData(submitData);

      this.subscriptions.push(
        apiCall.subscribe({
          next: (response: any) => {
            this.isLoading = false;
            console.log('Booking agent operation successful:', response);

            // Show success message
            Swal.fire({
              text: this.isEditMode
                ? "Booking agent has been successfully updated!"
                : "Booking agent has been successfully created!",
              icon: "success",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            }).then((result: any) => {
              if (result.isConfirmed) {
                // Close the modal
                this.closeModal();

                // Emit appropriate event
                if (this.isEditMode) {
                  this.bookingAgentUpdated.emit();
                } else {
                  this.bookingAgentAdded.emit();
                }

                // Reset the form
                this.resetForm();
              }
            });
          },
          error: (error: any) => {
            this.isLoading = false;
            console.error('Booking agent operation failed:', error);

            // Show error message
            Swal.fire({
              text: error.error?.message || "An error occurred. Please try again.",
              icon: "error",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            });
          }
        })
      );
    } else {
      this.markFormGroupTouched(this.bookingAgentForm);
    }
  }
}
